import React, { useState, useEffect } from 'react'
import { 
  Users, 
  Clock, 
  TrendingUp, 
  DollarSign, 
  MapPin,
  Calendar,
  RefreshCw,
  Activity,
  CheckCircle,
  XCircle,
  AlertCircle,
  Globe,
  Settings,
  LayoutDashboard
} from 'lucide-react'
import { format, subDays, startOfDay, endOfDay } from 'date-fns'
import { sessionAPI, huntAPI, analyticsAPI, healthAPI } from '../services/api'

const Dashboard = () => {
  const [sessions, setSessions] = useState([])
  const [hunts, setHunts] = useState([])
  const [analytics, setAnalytics] = useState(null)
  const [webhookHealth, setWebhookHealth] = useState(null)
  const [loading, setLoading] = useState(true)
  const [timeRange, setTimeRange] = useState('7') // days

  useEffect(() => {
    fetchData()
  }, [timeRange])

  const fetchData = async () => {
    try {
      setLoading(true)
      const [sessionsData, huntsData, analyticsData, healthData] = await Promise.all([
        sessionAPI.getAll(),
        huntAPI.getAll(),
        analyticsAPI.getOverview(parseInt(timeRange)),
        healthAPI.check().catch(() => null)
      ])

      setSessions(sessionsData.data || [])
      setHunts(huntsData.data || [])
      setAnalytics(analyticsData.success ? analyticsData.data : null)
      setWebhookHealth(healthData && healthData.success ? healthData : null)
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getRecentSessions = () => {
    const endDate = new Date()
    const startDate = subDays(endDate, parseInt(timeRange))
    
    return sessions.filter(session => {
      const sessionDate = new Date(session.created_at)
      return sessionDate >= startDate && sessionDate <= endDate
    })
  }

  const getSessionStats = () => {
    const recentSessions = getRecentSessions()
    const totalSessions = sessions.length
    
    return {
      total: totalSessions,
      recent: recentSessions.length,
      active: sessions.filter(s => s.status === 'active').length,
      completed: sessions.filter(s => s.status === 'completed').length,
      expired: sessions.filter(s => s.status === 'expired').length
    }
  }

  const getHuntStats = () => {
    return {
      total: hunts.length,
      active: hunts.filter(h => h.is_active).length,
      inactive: hunts.filter(h => !h.is_active).length
    }
  }

  const getRevenueStats = () => {
    if (!analytics) return { gross: 0, commission: 0, net: 0, commissionRate: 0 }
    
    return {
      gross: analytics.revenue?.gross || analytics.revenue?.total?.gross || 0,
      commission: analytics.revenue?.commission || analytics.revenue?.total?.commission || 0,
      net: analytics.revenue?.net || analytics.revenue?.total?.net || 0,
      commissionRate: analytics.revenue?.commissionRate || analytics.revenue?.total?.commissionRate || 0
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
      case 'configured':
      case 'active':
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'warning':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />
      case 'unhealthy':
      case 'error':
      case 'not configured':
        return <XCircle className="h-5 w-5 text-red-500" />
      default:
        return <Activity className="h-5 w-5 text-gray-500" />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
      case 'configured':
      case 'active':
        return 'text-green-600'
      case 'warning':
        return 'text-yellow-600'
      case 'unhealthy':
      case 'error':
      case 'not configured':
        return 'text-red-600'
      default:
        return 'text-gray-600'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  const sessionStats = getSessionStats()
  const huntStats = getHuntStats()
  const revenueStats = getRevenueStats()

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Clean header */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                <LayoutDashboard className="w-6 h-6 text-primary-600" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
                <p className="mt-1 text-gray-600">Overview of your scavenger hunt platform</p>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-4 sm:mt-0">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
              </select>
              <button
                onClick={fetchData}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Refresh data"
              >
                <RefreshCw size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {/* Total Sessions */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm hover-lift">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center shadow-sm">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-600">Total Sessions</p>
                <p className="text-2xl font-bold text-gray-900">{sessionStats.total}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {sessionStats.recent} in last {timeRange} days
                </p>
              </div>
            </div>
          </div>

          {/* Active Sessions */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm hover-lift">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center shadow-sm">
                  <Activity className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-600">Active Sessions</p>
                <p className="text-2xl font-bold text-gray-900">{sessionStats.active}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {sessionStats.completed} completed
                </p>
              </div>
            </div>
          </div>

          {/* Total Hunts */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm hover-lift">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center shadow-sm">
                  <MapPin className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-600">Total Hunts</p>
                <p className="text-2xl font-bold text-gray-900">{huntStats.total}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {huntStats.active} active
                </p>
              </div>
            </div>
          </div>

          {/* Revenue */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm hover-lift">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center shadow-sm">
                  <DollarSign className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-600">Revenue (30 days)</p>
                <p className="text-2xl font-bold text-gray-900">£{revenueStats.gross.toFixed(2)}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {revenueStats.commissionRate.toFixed(1)}% commission
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* System Health and Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* System Health */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">System Health</h2>
              <p className="text-sm text-gray-600">Current status of platform services</p>
            </div>
            <div className="p-6">
              {webhookHealth ? (
                <div className="space-y-4">
                  {/* Database */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm font-medium text-gray-700">Database</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(webhookHealth.services?.database?.status)}
                      <span className={`text-sm font-medium ${getStatusColor(webhookHealth.services?.database?.status)}`}>
                        {webhookHealth.services?.database?.status || 'Unknown'}
                      </span>
                    </div>
                  </div>

                  {/* Email Service */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm font-medium text-gray-700">Email Service</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(webhookHealth.services?.email?.status)}
                      <span className={`text-sm font-medium ${getStatusColor(webhookHealth.services?.email?.status)}`}>
                        {webhookHealth.services?.email?.status || 'Unknown'}
                      </span>
                    </div>
                  </div>

                  {/* Booking APIs */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm font-medium text-gray-700">Booking APIs</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(webhookHealth.services?.bookingApis?.status)}
                      <span className={`text-sm font-medium ${getStatusColor(webhookHealth.services?.bookingApis?.status)}`}>
                        {webhookHealth.services?.bookingApis?.status || 'Unknown'}
                      </span>
                    </div>
                  </div>

                  {/* Webhook Service */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm font-medium text-gray-700">Webhook Service</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(webhookHealth.services?.webhook?.status)}
                      <span className={`text-sm font-medium ${getStatusColor(webhookHealth.services?.webhook?.status)}`}>
                        {webhookHealth.services?.webhook?.status || 'Unknown'}
                      </span>
                    </div>
                  </div>

                  {/* GDPR Scheduler */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm font-medium text-gray-700">GDPR Scheduler</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(webhookHealth.services?.gdpr?.status)}
                      <span className={`text-sm font-medium ${getStatusColor(webhookHealth.services?.gdpr?.status)}`}>
                        {webhookHealth.services?.gdpr?.status || 'Unknown'}
                      </span>
                    </div>
                  </div>

                  {/* Overall Status */}
                  <div className="pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Overall Status</span>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(webhookHealth.overallStatus)}
                        <span className={`text-sm font-medium ${getStatusColor(webhookHealth.overallStatus)}`}>
                          {webhookHealth.overallStatus || 'Unknown'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Activity className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Health data unavailable</h3>
                  <p className="mt-1 text-sm text-gray-500">Unable to fetch system health information</p>
                </div>
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
              <p className="text-sm text-gray-600">Latest sessions and bookings</p>
            </div>
            <div className="p-6">
              {sessions.length > 0 ? (
                <div className="space-y-4">
                  {sessions.slice(0, 5).map((session) => (
                    <div key={session.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm text-gray-900">{session.customer_name}</span>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            session.status === 'completed' ? 'bg-green-100 text-green-800' :
                            session.status === 'active' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {session.status}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {session.customer_email} • {hunts.find(h => h.id === session.hunt_id)?.name || 'Unknown Hunt'}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-500">
                          {format(new Date(session.created_at), 'MMM d, yyyy')}
                        </div>
                        <div className="text-xs text-gray-400">
                          {format(new Date(session.created_at), 'HH:mm')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No sessions yet</h3>
                  <p className="mt-1 text-sm text-gray-500">Sessions will appear here once bookings start coming in</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <button className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-all duration-200 text-left group hover-lift">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary-100 rounded-lg group-hover:bg-primary-200 transition-colors shadow-sm">
                  <MapPin className="h-5 w-5 text-primary-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Create Hunt</p>
                  <p className="text-sm text-gray-500">Add new scavenger hunt</p>
                </div>
              </div>
            </button>

            <button className="p-4 border border-gray-200 rounded-lg hover:border-green-300 hover:bg-green-50 transition-all duration-200 text-left group hover-lift">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg group-hover:bg-green-200 transition-colors shadow-sm">
                  <Users className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">View Sessions</p>
                  <p className="text-sm text-gray-500">Monitor active sessions</p>
                </div>
              </div>
            </button>

            <button className="p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-all duration-200 text-left group hover-lift">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-colors shadow-sm">
                  <TrendingUp className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Analytics</p>
                  <p className="text-sm text-gray-500">View performance data</p>
                </div>
              </div>
            </button>

            <button className="p-4 border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-all duration-200 text-left group hover-lift">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 rounded-lg group-hover:bg-gray-200 transition-colors shadow-sm">
                  <Settings className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Settings</p>
                  <p className="text-sm text-gray-500">Configure platform</p>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
